import { useEffect, useRef, useState } from 'react';
import {
  IModalContent,
  INlpRequest,
  IQuestionOption,
  MessageType,
  Modals,
  TNullable,
} from '@common/types';
import { MainAgreementBanner } from '@components/agreement-banner';
import { GenericModal } from '@components/generic-modal';
import MED_INFO_CONTENT from '@constants/med-info-content';
import useChat from '@hooks/use-chat';
import { cn, CURRENT_INSTANCE, shouldUseMaxHight } from '@lib/utils';
import { useTranslation } from 'react-i18next';
import ChoiceResponse from './choice-response';
import CompleteResponse from './complete-response';
import InvalidQueryResponse from './invalid-query-response';
import LoadingMessage from './loading-message';
import NoAnswerResponse from './no-answer-response';
import SimpleResponse from './simple-response';
import UserMessage from './user-message';

interface IChatStreamProps {
  request: TNullable<INlpRequest>;
  onShowMoreAnswers: (messageId: number) => void;
  onSelectChoice: (choice: IQuestionOption | undefined) => void;
  isLoading: boolean;
}

function ChatStream(props: IChatStreamProps) {
  const { t } = useTranslation();
  const { messages, showAgreementBanner } = useChat();
  const [modalContent, setModalContent] = useState<IModalContent>();
  const chatStreamContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatStreamContainerRef?.current?.scroll({
      top: chatStreamContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [props.isLoading, chatStreamContainerRef, messages]);

  function handleModalShow(type: Modals, userQuery?: string) {
    if (Modals.MedicalInfo === type) {
      const text = MED_INFO_CONTENT[CURRENT_INSTANCE.name].de;
      const title = t('chat.medical_info.title');
      setModalContent({ text, title, userQuery });
      return;
    }
    setModalContent(undefined);
  }

  return (
    <div
      // [hexal]
      className={cn(
        'w-full px-2 sm:px-0 sm:w-[calc(100%-20px)] mt-4 overflow-y-auto max-h-[calc(100vh-220px)] sm:max-h-[calc(100vh-200px)] bg-xircle-main custom-scroll',
        shouldUseMaxHight() && '!max-h-[600px] sm:!max-h-[500px]'
      )}
      ref={chatStreamContainerRef}
    >
      {showAgreementBanner && <MainAgreementBanner />}
      <div className="flex flex-col justify-start">
        {messages.map((message, index) => {
          const lastUserQuery = [...messages.slice(0, index + 1)]
            .reverse()
            .find((item) => item.type === MessageType.UserEntry)?.message;

          if (message.type === MessageType.UserEntry) {
            return <UserMessage message={message.message} key={message.id} />;
          }
          if (
            message.type === MessageType.Message ||
            message.type === MessageType.Error ||
            message.type === MessageType.DisabledTopicMessage
          ) {
            return (
              <SimpleResponse
                message={message}
                key={message.id}
                showModal={handleModalShow}
                userQuery={lastUserQuery}
              />
            );
          }
          if (message.type === MessageType.Response) {
            return (
              <CompleteResponse
                key={message.id}
                message={message}
                onShowMoreAnswers={props.onShowMoreAnswers}
                showModal={handleModalShow}
                hasMoreOptions={message.choices && message.choices.length > 1}
                userQuery={lastUserQuery}
              />
            );
          }
          if (message.type === MessageType.ChoiceResponse) {
            return (
              <ChoiceResponse
                key={message.id}
                message={message}
                onSelectChoice={props.onSelectChoice}
              />
            );
          }
          if (
            message.type === MessageType.NoAnswerResponse ||
            message.type === MessageType.ForceMedInfo
          ) {
            return <NoAnswerResponse key={message.id} userQuery={lastUserQuery} />;
          }
          if (message.type === MessageType.InvalidQuery) {
            return <InvalidQueryResponse key={message.id} />;
          }
        })}
        {modalContent && (
          <GenericModal
            open={!!modalContent}
            onOpenChange={() => setModalContent(undefined)}
            title={modalContent.title}
            description={modalContent.text}
            userQuery={modalContent.userQuery}
          />
        )}
        {props.isLoading && <LoadingMessage />}
      </div>
    </div>
  );
}

export default ChatStream;
